import { createSelector } from '@ngrx/store';
import { addMinutes } from 'date-fns';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import {
  CardLinkType,
  MissionRoleType,
  TransportOfferResponse,
  TransportOfferStatus,
} from 'src/graphql/generated/graphql.generated';
import { RequestViewModel } from 'src/models/request-view-model';
import {
  CardSummaryRecord,
  selectAllCardSummaryEntities,
} from 'src/store/generated/card-summary-record.generated';
import {
  TransportOfferRecord,
  TransportOfferViewModelBase,
} from 'src/store/generated/transport-offer-record.generated';
import { formatDate } from 'src/util/date';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { humanize } from 'src/util/text';
import { getOrElse } from 'src/util/values';
import { shareReplaySafe } from 'src/util/rxjs/operators/share-replay-safe';
import { CompanyViewModel } from './company-view-model';
import { UnitSummaryViewModel } from './unit-summary-view-model';
import { VehicleSummaryViewModel } from './vehicle-summary-view-model';

export interface OfferAttributeViewModel {
  label: string;
  value: string | number | null;
  valueClass?: string;
  tooltip?: string;
}
export interface OfferSummaryViewModel {
  description: string;
  descriptionClass?: string;
  status?: string;
  statusClass?: string;
  attributes: OfferAttributeViewModel[];
}
export interface TransportOfferEstimatedValues {
  etloInMinutes: number;
  eteInMinutes: number;
  etaInMinutes: number;
  onBaseEtaInMinutes: number;
  specialityTeamEtlo: number;
  specialityTeamEte: number;
  etcInMinutes: number | null;
  times: { [zone: string]: Date };
  displayTime: (date: Date | number | string) => string;
}
export function isPriorOffer({
  status,
  response,
}: {
  status: TransportOfferStatus;
  response: TransportOfferResponse;
}): boolean {
  return (
    status === TransportOfferStatus.Skipped ||
    status === TransportOfferStatus.Cancelled ||
    response === TransportOfferResponse.Declined ||
    response === TransportOfferResponse.Aborted
  );
}
function offerAttributes(offer: TransportOfferViewModel): OfferAttributeViewModel[] {
  return [
    {
      label: 'Distance',
      value: `${offer.distanceInNauticalMiles}nm`,
      tooltip: `${offer.distanceInMiles}mi`,
    },
    { label: 'Heading', value: offer.heading },
    { label: 'Ground Leg', value: 'N/A' },
  ];
}
function createViewModelFromUnitSummary(
  offer: TransportOfferViewModel,
  unitSummary: UnitSummaryViewModel,
  vehicleSummary?: VehicleSummaryViewModel,
) {
  let statusRollup = `${humanize(unitSummary.status)}`;
  if (vehicleSummary != null) {
    if (vehicleSummary.isEnRoute) {
      const destination = vehicleSummary.to != null ? ` ${humanize(vehicleSummary.to.type)}` : '';
      statusRollup = `${humanize(vehicleSummary.status)}${destination}`;
    } else {
      const currentWaypointDisplay =
        vehicleSummary.from != null ? humanize(vehicleSummary.from.type) : '';
      statusRollup = `${humanize(
        vehicleSummary.statusDescription.includes('Standby') ? 'Standby' : unitSummary.status,
      )} at ${currentWaypointDisplay}`;
      if (unitSummary.isAvailable && unitSummary.isCrewAvailable === false) {
        statusRollup = `${humanize(unitSummary.status)} w/o Crew at ${currentWaypointDisplay}`;
      }
    }
  }
  return {
    description: unitSummary.abbreviation,
    descriptionClass: `unit__abbreviation--${unitSummary.status}`,
    status: statusRollup,
    statusClass: `unit__header--${unitSummary.status}`,
    attributes: [
      ...offerAttributes(offer),
      {
        label: 'Wx at Base',
        value: unitSummary.weatherStatus,
        valueClass: `unit__weather--${unitSummary.weatherStatus}`,
      },
    ],
  };
}
function createViewModelForInternalReferral(offer: TransportOfferViewModel): OfferSummaryViewModel {
  return {
    description: offer.unitDescription,
    status: 'Unknown',
    attributes: offerAttributes(offer),
  };
}
function createViewModelForExternalReferral(offer: TransportOfferViewModel): OfferSummaryViewModel {
  return {
    description: offer.unitDescription,
    status: 'Unknown',
    attributes: offerAttributes(offer),
  };
}
export function selectCardSummaryByRequestId(vm: ViewModelService, requestId: string) {
  return vm.store
    .select(
      createSelector(selectAllCardSummaryEntities, (entities) =>
        entities
          .filter((e) => (e.request == null ? false : e.request.id === requestId))
          .valueSeq()
          .first<CardSummaryRecord>(undefined),
      ),
    )
    .pipe(undefinedOrSwitchMap((cardSummaryRecord) => vm.getCardSummaryById(cardSummaryRecord.id)));
}
export class TransportOfferViewModel extends TransportOfferViewModelBase {
  public readonly request: RequestViewModel;
  public readonly summary$: Observable<OfferSummaryViewModel>;
  public readonly unitSummary$: Observable<UnitSummaryViewModel | undefined>;
  public readonly estimatedValues$: Observable<TransportOfferEstimatedValues>;
  public readonly canOffer$: Observable<
    { canOffer: true } | { canOffer: false; reasons: string[] }
  >;
  public readonly isSpecialityTeamSelected$: Observable<boolean>;
  public readonly isTeamFlight$: Observable<boolean>;
  public readonly configureUnitText =
    'Configure the unit by transferring crew members from an out-of-service unit to this unit.';

  get isReferral(): boolean {
    return this._props.isReferral === true;
  }

  get isExternal(): boolean {
    return this.isReferral && this.unitId == null;
  }

  get isSisterCompany(): boolean {
    return this.isReferral && this.unitId != null;
  }

  get isPriorOffer(): boolean {
    return isPriorOffer(this);
  }

  get isAutoDeclinable$() {
    return combineLatest([this._hasAutoDeclineOfferId$, this.unitSummary$]).pipe(
      map(
        ([hasId, unit]) =>
          hasId &&
          (this.request.isOffering || this.request.isAssignmentDelayed) &&
          (unit == null || !unit.isAvailable),
      ),
    );
  }

  get canOfferAutoDeclinable$() {
    return combineLatest([this._hasAutoDeclineOfferId$, this.unitSummary$]).pipe(
      map(([hasId, unit]) => hasId && this.request.isOffering && unit != null && unit.isAvailable),
    );
  }

  get allowStandby() {
    return (
      !this.isReferral &&
      this.isOffered &&
      (this.request.isSceneCall ||
        this.request.isTransfer ||
        this.request.isPublicRelations ||
        this.request.isSearchAndAssist ||
        this.request.isPublicUtility ||
        this.request.isResidence)
    );
  }

  get canPatientOriginateAtUnit() {
    return (
      !this.isReferral && this.isOffered && (this.request.isSceneCall || this.request.isTransfer)
    );
  }

  get referralTypeDisplay(): 'External' | null {
    if (this.isReferral) {
      return this.isSisterCompany ? null : 'External';
    }
    return null;
  }

  get heading() {
    if (this.bearingInDegrees != null) {
      return this.bearingInDegrees.toFixed(0);
    }
    return this.bearingInDegrees;
  }

  get distanceInNauticalMiles() {
    return this.distanceInMiles != null ? (this.distanceInMiles * 0.868976).toFixed(0) : undefined;
  }

  get unitDescription(): string {
    return getOrElse(this._props.unitDescription, 'Unknown');
  }

  get estimatedDepartureTimeInMinutes(): number {
    return getOrElse(this._props.estimatedDepartureTimeInMinutes, 0);
  }

  get estimatedTimeEnRouteInMinutes(): number {
    return getOrElse(this._props.estimatedTimeEnRouteInMinutes, 0);
  }

  get estimatedTimeToArrivalInMinutes(): number {
    return (
      this.estimatedDepartureTimeInMinutes +
      this.estimatedTimeEnRouteInMinutes +
      this.specialtyTeamETLO +
      this.specialtyTeamETE +
      this.estimatedTimeToCrewInMinutes
    );
  }

  get onBaseETAInMinutes(): number {
    return getOrElse(this._props.onBaseETAInMinutes, 0);
  }

  get specialtyTeamETE(): number {
    return getOrElse(this._props.specialtyTeamETEInMinutes, 0);
  }

  get specialtyTeamETLO(): number {
    return getOrElse(this._props.specialtyTeamETLOInMinutes, 0);
  }

  get estimatedTimeToCrewInMinutes(): number {
    return getOrElse(this._props.etc, 0);
  }

  get offerReponseHeader(): string {
    if (this.isAborted || this.isDeclined) {
      return this.response;
    } else if (this.isSkipped) {
      return this.statusDisplay;
    } else if (this.isCancelled) {
      return 'Cancelled';
    } else {
      return 'NA';
    }
  }

  get statusDisplay(): string {
    if (this.status !== TransportOfferStatus.Skipped) {
      return this.status;
    }
    return this.wasBulkSkip ? `${this.status} (Bulk)` : this.status;
  }

  get initialDestinationIsInHawaii$(): Observable<boolean> {
    return this.request.initialDestination$.pipe(
      map(
        (initialDestination) =>
          initialDestination?.address?.region === 'Hawaii' ||
          initialDestination?.address?.region === 'HI',
      ),
    );
  }

  constructor(
    transportOffer: TransportOfferRecord,
    request: RequestViewModel,
    public readonly company: CompanyViewModel | undefined,
    private _model: ViewModelService,
  ) {
    super(transportOffer);
    this.request = request;

    this.unitSummary$ =
      this.unitId != null
        ? this._model.getUnitSummaryById(this.unitId).pipe(shareReplaySafe(1))
        : of(undefined);
    const vehicleSummary$ = this.unitSummary$.pipe(
      undefinedOrSwitchMap((unitSummary) => {
        if (unitSummary.vehicleSummaryId == null) {
          return of(undefined);
        }
        return this._model.getVehicleSummaryById(unitSummary.vehicleSummaryId);
      }),
    );
    this.summary$ = combineLatest([this.unitSummary$, vehicleSummary$]).pipe(
      switchMap(([unitSummary, vehicleSummary]) => {
        return unitSummary != null
          ? of(createViewModelFromUnitSummary(this, unitSummary, vehicleSummary))
          : this.unitId == null
          ? of(createViewModelForExternalReferral(this))
          : of(createViewModelForInternalReferral(this));
      }),
      shareReplaySafe(1),
    );

    this.isSpecialityTeamSelected$ = this.request.requester$.pipe(
      map((value) => value?.specialtyTeamId != null),
    );

    this.isTeamFlight$ = this.request.requester$.pipe(
      map((requester) =>
        requester == null ? false : requester.missionRole === MissionRoleType.TeamFlight,
      ),
    );

    this.canOffer$ = combineLatest([
      this.unitSummary$,
      this.request.card.cardLinks$,
      this.request.requester$,
    ]).pipe(
      map(([unit, cardLinks, requester]) => {
        const reasons: string[] = [];
        // Card state conditions
        if (this.request.card.isClosed) {
          reasons.push('Card is closed.');
        }
        if (!this.request.card.isAwaitingAssignment) {
          reasons.push('Card is not awaiting assignment.');
        }
        // Request conditions
        if (this.request.isCancelled) {
          reasons.push('Request is cancelled.');
        }
        if (this.request.isAssigned) {
          reasons.push('Request has already been assigned.');
        }
        if (!this.request.isOffering && !this.request.isAssignmentDelayed) {
          reasons.push('This request is currently not available for offering.');
        }
        // Transport offer conditions
        if (this.isOffered && this.isPending) {
          reasons.push(
            'This transport offer is currently being offered and waiting for a response.',
          );
        } else if (this.request.isOffered) {
          reasons.push(
            'This request is currently being offered to another transport offer and waiting for a response.',
          );
        } else if (
          this.isSisterCompany &&
          this.request.card.linkedCardId != null &&
          cardLinks.find((cl) => cl.type === CardLinkType.Referral) != null
        ) {
          reasons.push('Referral requests cannot be offered to another company.');
        }
        // Unit conditions
        if (unit != null) {
          if (unit.isCommitted) {
            reasons.push('Unit is committed.');
          }
          if (unit.isOutOfService) {
            reasons.push('Unit is out of service.');
          }
          const missingResources = unit.missingResources(this.isGround);
          const isSpecialtyTeamRequest = requester?.missionRole === MissionRoleType.TeamFlight;
          const allowMissingResourcesForSpecialtyTeam =
            isSpecialtyTeamRequest && unit.isPartiallyAvailable;
          if (missingResources !== false) {
            // Allow units to be offered if they are partially available for specialty team requests
            if (!allowMissingResourcesForSpecialtyTeam) {
              reasons.push(`Unit is missing resources (${missingResources.summary}).`);
            }
          }
        }
        if (reasons.length > 0) {
          return { canOffer: false, reasons };
        }
        return { canOffer: true };
      }),
      shareReplaySafe<any>(1),
    );

    const patientOriginatesAtUnit = this.patientOriginatesAtUnit ?? false;
    const etaInMinutes$ = of(
      patientOriginatesAtUnit ? this.onBaseETAInMinutes ?? 0 : this.estimatedTimeToArrivalInMinutes,
    );
    this.estimatedValues$ = combineLatest([etaInMinutes$, this.isSpecialityTeamSelected$]).pipe(
      map(([etaInMinutes, isSpecialityTeamSelected]) => {
        if (isSpecialityTeamSelected && patientOriginatesAtUnit) {
          etaInMinutes =
            this.estimatedTimeToArrivalInMinutes +
            this.onBaseETAInMinutes -
            this.estimatedDepartureTimeInMinutes;
        }
        return etaInMinutes;
      }),
      map((etaInMinutes) => {
        return {
          etloInMinutes: this.estimatedDepartureTimeInMinutes,
          eteInMinutes: this.estimatedTimeEnRouteInMinutes,
          etaInMinutes,
          onBaseEtaInMinutes: this.onBaseETAInMinutes,
          specialityTeamEte: this.specialtyTeamETE,
          specialityTeamEtlo: this.specialtyTeamETLO,
          etcInMinutes: this.etc,
          times: {
            local: addMinutes(Date.now(), etaInMinutes),
          },
          displayTime: (date: Date | number | string): string => {
            return formatDate(date, 'HH:mm');
          },
        };
      }),
    );
  }

  private get _hasAutoDeclineOfferId$() {
    return this.request.autoDeclineOfferIds$.pipe(
      map((autoDeclineOfferIds) => {
        return this.declineReasonId == null
          ? false
          : autoDeclineOfferIds.find((id) => id === this.id) != null;
      }),
    );
  }
}
